import { mulberry32, pickIndex, type Rng } from "./rng";
import { RUN_COLUMNS, type NodeKind, type NodeModifier, type RunNode } from "./run-types";

// Seeded run-map (DAG) generation, Slay-the-Spire shape: short columns of
// nodes with forward edges, every node reachable from column 0 and every
// node able to reach the single final node.
//
// Column plan (RUN_COLUMNS = 5):
//   col 0: 2 combat            — opening choice
//   col 1: 3 nodes             — one may roll elite
//   col 2: 2-3 nodes           — exactly one resupply, rest combat/elite
//   col 3: 2 nodes             — at least one elite (pre-final spike)
//   col 4: 1 final             — the full exfil finale
//
// Variant indices are rolled per node so the map screen can show which part
// of the valley each fight covers (see mission/variants.ts VALLEY_VARIANTS).

const COMBAT_VARIANTS = 3; // variant pool size for combat/elite nodes
const FINAL_VARIANT = 3; // reserved template index for the finale

const ALL_KINDS: readonly NodeKind[] = ["combat", "elite", "defend", "destroy", "ambush", "delay", "sead", "extraction", "rescue", "resupply", "final"];

// Mid-run combat slots can roll into specialist mission types — each is a
// different tactical problem (docs/design/MISSION_VARIETY.md). Taskings the
// campaign hasn't authorized yet (meta/campaign.ts unlockedNodeKinds) fall
// back to escort; the roll is consumed either way so the same seed replays
// the same graph under a fixed allowed set.
function specialistOrCombat(rng: Rng, allowed: ReadonlySet<NodeKind>): NodeKind {
  const r = rng();
  const kind: NodeKind =
    r < 0.12 ? "defend"
    : r < 0.22 ? "destroy"
    : r < 0.32 ? "ambush"
    : r < 0.41 ? "delay"
    : r < 0.5 ? "sead"
    : r < 0.58 ? "extraction"
    : r < 0.66 ? "rescue"
    : "combat";
  return allowed.has(kind) ? kind : "combat";
}

function rollKinds(rng: Rng, allowed: ReadonlySet<NodeKind>): NodeKind[][] {
  // Column 0 stays pure escort — it teaches the core loop.
  const col1: NodeKind[] = [specialistOrCombat(rng, allowed), specialistOrCombat(rng, allowed), "combat"];
  if (rng() < 0.35) col1[2] = "elite";

  const col2Size = rng() < 0.5 ? 2 : 3;
  const col2: NodeKind[] = Array.from({ length: col2Size }, () =>
    rng() < 0.3 ? "elite" : specialistOrCombat(rng, allowed));
  col2[pickIndex(rng, col2Size)] = "resupply";

  const col3: NodeKind[] = [specialistOrCombat(rng, allowed), "combat"];
  col3[pickIndex(rng, 2)] = "elite";
  if (rng() < 0.25) col3.fill("elite"); // occasionally both pre-final fights spike

  return [["combat", "combat"], col1, col2, col3, ["final"]];
}

export function generateRunMap(seed: number, allowedKinds: ReadonlySet<NodeKind> = new Set(ALL_KINDS)): RunNode[] {
  const rng = mulberry32(seed ^ 0x9e3779b9);
  const kindsByCol = rollKinds(rng, allowedKinds);
  if (kindsByCol.length !== RUN_COLUMNS) {
    throw new Error("run-map: column plan out of sync with RUN_COLUMNS");
  }

  const nodes: RunNode[] = [];
  const byCol: RunNode[][] = kindsByCol.map((kinds, col) =>
    kinds.map((kind, row) => {
      // Combat variants avoid repeating the previous column's same row where
      // possible, purely for variety; the finale uses its reserved template.
      const variant = kind === "final"
        ? FINAL_VARIANT
        : kind === "resupply"
          ? 0
          : pickIndex(rng, COMBAT_VARIANTS);
      const node: RunNode = { id: `n${col}-${row}`, kind, col, row, variant, next: [] };
      // Some sorties fly pre-dawn (thermal default) — variety within a kind
      const rollable = kind !== "resupply" && kind !== "final";
      if (rollable && kind !== "defend" && rng() < 0.25) {
        node.night = true;
      }
      // One mission modifier, 30% of fights past the opening column. The
      // single roll decides both the chance and the pick.
      if (rollable && col >= 1) {
        const m = rng();
        if (m < 0.3) {
          const mods: NodeModifier[] = ["storm", "escalating", "aa-dense", "heavy-armor"];
          node.modifier = mods[Math.min(mods.length - 1, Math.floor((m / 0.3) * mods.length))];
        }
      }
      nodes.push(node);
      return node;
    }),
  );

  // Forward edges: walk adjacent rows so lanes cross but never fully tangle.
  // Constraints: every node has ≥1 outgoing edge, every next-column node has
  // ≥1 incoming edge.
  for (let col = 0; col < RUN_COLUMNS - 1; col++) {
    const from = byCol[col]!;
    const to = byCol[col + 1]!;
    const covered = new Set<number>();

    from.forEach((node, i) => {
      // Map this node's row onto the next column's row space
      const center = to.length === 1 ? 0 : Math.round((i / Math.max(1, from.length - 1)) * (to.length - 1));
      const targets = new Set<number>([center]);
      // Roll an extra edge to a neighbor for branching
      if (to.length > 1 && rng() < 0.6) {
        const step = rng() < 0.5 ? -1 : 1;
        const alt = Math.min(to.length - 1, Math.max(0, center + step));
        targets.add(alt);
      }
      node.next = [...targets].sort((a, b) => a - b).map((r) => to[r]!.id);
      targets.forEach((r) => covered.add(r));
    });

    // Guarantee every next-column node is reachable: attach orphans to the
    // nearest source node by row ratio.
    to.forEach((target, r) => {
      if (covered.has(r)) return;
      const srcIdx = from.length === 1 ? 0 : Math.round((r / Math.max(1, to.length - 1)) * (from.length - 1));
      const src = from[srcIdx]!;
      if (!src.next.includes(target.id)) src.next.push(target.id);
      covered.add(r);
    });
  }

  return nodes;
}

export function nodesInColumn(nodes: readonly RunNode[], col: number): RunNode[] {
  return nodes.filter((n) => n.col === col).sort((a, b) => a.row - b.row);
}

export function nodeById(nodes: readonly RunNode[], id: string): RunNode | undefined {
  return nodes.find((n) => n.id === id);
}
